<template>
    <div class="">
        <p v-if="ui.selection.text" class="mt-6 font-normal text-center text-blue-500">
            {{ ui.selection.text }}
        </p>

        <BaseLabel class="mt-5" text="Search by" :validation="$v.searchBy">
            <BaseSelect
                :value="searchBy"
                :options="searchOptions"
                :disabled="isLoading"
                :settings="{ label: 'label' }"
                @input="$emit('update:searchBy', $event)"
            />
        </BaseLabel>

        <transition name="slide" mode="out-in">
            <!-- BY DATE -->
            <!-- <div v-if="searchBy === 'ByDate'">
                <BaseLabel class="mt-5" text="Date" :validation="$v.preferredDate">
                    <DatePicker
                        :disabled="isLoading"
                        output-format="d/M/yy"
                        @select="handleSelectDate"
                    />
                </BaseLabel>
                <transition name="slide" mode="out-in">
                    <p v-if="!filteredAppointments.length && !isLoading" class="mt-4 text-blue-500">
                        We're sorry, there are no appointments on this date - try searching another
                        date or 'next available'.
                    </p>
                </transition>
            </div> -->

            <!-- BY PRACTITIONER -->
            <div
                v-if="searchBy === 'ByPractitioner'"
                role="radiogroup"
                :aria-labelledby="practitionerType"
                class="mt-6"
            >
                <BaseLabel
                    class="mt-5"
                    :text="practitionerType"
                    :validation="$v.preferredPractitionerId"
                >
                    <PractitionerList
                        :practitioners="practitioners"
                        :practitioner-id="preferredPractitionerId"
                        :appointments="appointments"
                        :disabled="isLoading"
                        @select="$emit('update:preferredPractitionerId', $event)"
                    />
                </BaseLabel>
            </div>

            <!-- BY NEXT AVAILABLE -->
            <div v-if="searchBy === 'ByNextAvailable'" class="mt-6">
                <BaseLabel class="mt-5" text="Date">
                    <DateCarousel
                        :is-loading="isLoading"
                        :appointments="appointments"
                        :preferred-date="preferredDate"
                        @select="$emit('update:preferredDate', $event)"
                    />
                </BaseLabel>

                <transition name="slide" mode="out-in">
                    <BaseLabel v-if="preferredDate" class="mt-5" text="Appointments">
                        <p
                            class="mb-1 text-center bg-blue-100 rounded-t rounded-t-xl small text-grey-500"
                        >
                            {{ preferredDateFormatted }}
                        </p>
                        <div class="flex flex-wrap -mx-1">
                            <div
                                v-for="(timeSlot, index) in appointmentTimesOnDate"
                                :key="index"
                                class="w-1/3 px-1 my-1"
                            >
                                <BaseRadio
                                    :active="preferredTime && timeSlot == preferredTime"
                                    :value="timeSlot"
                                    :text="timeSlot"
                                    @input="$emit('update:preferredTime', $event)"
                                />
                            </div>
                        </div>
                    </BaseLabel>
                </transition>
            </div>

            <!-- BY DAY -->
            <div v-if="searchBy === 'ByDay'" class="mt-6">
                <BaseLabel class="mt-5" text="Day of week">
                    <BaseSelect
                        :value="weekday"
                        :options="weekdays"
                        :disabled="isLoading"
                        :settings="{ label: 'label' }"
                        @input="$emit('update-weekday', $event)"
                    />
                </BaseLabel>

                <transition-group name="slide" mode="out-in">
                    <p
                        v-if="weekday && appointments.length === 0"
                        key="1"
                        class="mt-3 text-blue-500"
                    >
                        There are no upcoming appointments available on that day. Please choose
                        another day.
                    </p>

                    <div v-else-if="weekday" key="2">
                        <BaseLabel key="2" class="mt-5" text="Date">
                            <DatePicker
                                :appointments="appointments"
                                :value="preferredDate"
                                :disabled="isLoading"
                                @select="$emit('update:preferredDate', $event)"
                            />
                        </BaseLabel>

                        <transition name="slide" mode="out-in">
                            <div v-if="preferredDate" key="3" class="flex flex-wrap mt-1 -mx-1">
                                <div
                                    v-for="(timeSlot, index) in appointmentTimesOnDate"
                                    :key="index"
                                    class="w-1/3 px-1 my-1 text-blue-500"
                                >
                                    <BaseRadio
                                        :active="preferredTime && timeSlot == preferredTime"
                                        :value="timeSlot"
                                        :text="timeSlot"
                                        :disabled="isLoading"
                                        @input="$emit('update:preferredTime', $event)"
                                    />
                                </div>
                            </div>
                        </transition>
                    </div>
                </transition-group>
            </div>
        </transition>

        <!-- <button
            class="w-full mt-10 btn exception"
            :disabled="$v.$invalid"
            :class="{
                'btn--ghost-grey opacity-75': $v.$invalid && !isLoading,
                'btn--loading text-transparent': isLoading
            }"
            @click="submit"
        >
            Continue
        </button> -->
    </div>
</template>

<script>
import { DateTime } from 'luxon';
import { toDateString, weekdays } from '@/../vue/online-booking/utils/dates';
import { requiredIf, required } from 'vuelidate/lib/validators';
import BaseLabel from '@/../vue/_base/form/Label.vue';
import BaseRadio from '@/../vue/_base/form/Radio.vue';
import BaseSelect from '@/../vue/_base/form/Select.vue';
import PractitionerList from '@/../vue/online-booking/components/PractitionerList.vue';
import DateCarousel from '@/../vue/online-booking/components/DateCarousel.vue';
import DatePicker from '@/../vue/online-booking/components/DatePicker.vue';
import ServiceTypeUtils from '@/../vue/online-booking/mixins/service-type-utils';
import appointmentFilters from '@/../vue/online-booking/mixins/appointment-filters';

export default {
    components: {
        BaseSelect,
        BaseLabel,
        BaseRadio,
        PractitionerList,
        DateCarousel,
        DatePicker
    },

    mixins: [ServiceTypeUtils, appointmentFilters],

    props: {
        practitioners: {
            type: Array,
            required: true
        },
        preferredTime: {
            type: String,
            default: null
        },
        preferredDate: {
            type: String,
            default: null
        },
        weekday: {
            type: String,
            default: null
        },
        preferredPractitionerId: {
            type: Number,
            default: null
        },
        serviceType: {
            type: String,
            required: true
        },
        appointments: {
            type: Array,
            required: true
        },
        ui: {
            type: Object,
            required: true
        },
        searchBy: {
            type: String,
            default: null
        },
        isLoading: {
            type: Boolean,
            required: true
        }
    },

    data: () => ({
        weekdays
    }),

    validations: {
        searchBy: {
            required
        },
        preferredPractitionerId: {
            required: requiredIf(function () {
                return this.searchBy === 'ByPractitioner';
            })
        },
        preferredDate: {
            required: requiredIf(function () {
                return ['ByDate', 'ByNextAvailable'].includes(this.searchBy);
            })
        },
        preferredTime: {
            required: requiredIf(function () {
                return ['ByDay', 'ByNextAvailable'].includes(this.searchBy);
            })
        },
        weekday: {
            required: requiredIf(function () {
                return this.searchBy === 'ByDay';
            })
        },
        filteredAppointments: {
            required
        }
    },

    computed: {
        searchOptions() {
            return [
                {
                    label: this.practitionerType,
                    value: 'ByPractitioner'
                },
                // {
                //     label: 'Date',
                //     value: 'ByDate'
                // },
                {
                    label: 'Next Available',
                    value: 'ByNextAvailable'
                },
                {
                    label: 'Day of week',
                    value: 'ByDay'
                }
            ];
        },
        preferredDateFormatted() {
            return (
                this.preferredDate &&
                DateTime.fromFormat(this.preferredDate, 'd/M/yy').toFormat('cccc d, MMMM yyyy')
            );
        }
    },

    watch: {
        weekday() {
            // Set preferred date to nearest date on chosen day.
            const nearestAptOnChosenDay = this.appointments.find((apt) => {
                return toDateString(apt.appointmentDate, 'ccc').toLowerCase() === this.weekday;
            });
            if (nearestAptOnChosenDay) {
                const formattedDate = toDateString(nearestAptOnChosenDay.appointmentDate);
                this.$emit('update:preferredDate', formattedDate);
            }
        },

        $v: {
            handler() {
                if (!this.$v.$invalid) {
                    this.submit();
                }
            },
            deep: true
        }
    },

    methods: {
        handleSelectDate(dateString) {
            this.$emit('update:preferredDate', dateString);
            this.$emit('update-date-range', dateString);
        },
        submit() {
            // Re-fetch appointments from API for specific practitioner. Without practitionId , response emitts some appointments.
            if (this.searchBy === 'ByPractitioner') this.$emit('update-practitioner');

            if (!this.isLoading) this.$emit('next');
        }
    }
};
</script>
